/**
 * Basket add-to-cart rules
 *
 * Rules enforced at runtime, everywhere an Add to Cart button is rendered (product pages,
 * product cards, quick-add buttons, featured product sections, collection pages, search
 * results, recommendations, any `form[action*="/cart/add"]`):
 *
 *   1. If the cart holds any product whose product type is "Basket", every Add to Cart
 *      button on the site is disabled. The Checkout button is NOT touched, so the
 *      customer can finish the Basket order.
 *   2. If the cart holds only non-Basket products, nothing is restricted.
 *   3. If the cart holds a non-Basket product and the customer views a Basket product,
 *      that Basket product's Add to Cart button is disabled and a notice is shown
 *      explaining that Baskets must be ordered separately.
 *   4. If the cart holds a Basket AND a non-Basket product at the same time, every
 *      Checkout control (Check out button, express wallet buttons, Enter-key submit of
 *      the cart form) is disabled and a notice is shown above each `.cart__ctas` block.
 *   5. When the Basket is removed from the cart (or the cart is emptied), everything is
 *      re-enabled immediately.
 *
 * Source of truth for the cart is `/cart.js` (exposes `product_type` per line item).
 * The product type of each Add to Cart form is resolved, in order, from:
 *   - a `data-product-type` attribute on the form or an ancestor (optional Liquid hook),
 *   - `window.ShopifyAnalytics.meta.product.type` for the main product form,
 *   - `/products/{handle}.js`, cached per handle. The handle is found by walking up from
 *     the button (form -> add-to-cart-component -> product-card ...) until an ancestor
 *     carries `data-product-handle` / `data-product-url` / a product link, or contains
 *     exactly one product link. The walk never crosses a card boundary or a section, so
 *     collection grids cannot leak another card's handle.
 *
 * Independence: this file only reads `/cart.js` and listens to events. It never imports
 * or calls the theme's cart drawer, never dispatches cart events, and only ever changes
 * elements it marked itself with `data-basket-atc-disabled`, so it can run alongside
 * `cart-drawer.js` (and the gift basket cart rules) without touching their behaviour.
 * No Liquid changes are needed. Re-evaluation is triggered by the theme's cart events
 * (when the `@shopify/events` import map entry exists), by any `fetch()` to the cart
 * AJAX endpoints, and, as a guarantee that does not depend on event names, by a
 * MutationObserver that watches for cart or product-form markup being re-rendered.
 *
 * Install: upload to assets/ and add to layout/theme.liquid (before </body>):
 *   <script type="module" src="{{ 'basket-add-to-cart-rules.js' | asset_url }}"></script>
 */

/** Product types (lower-cased) that count as a Basket. */
const BASKET_TYPES = new Set(['gift-basket', 'gift basket', 'basket']);

const CONFLICT_NOTICE_TEXT =
  'Your cart already contains an item. Basket products must be ordered separately, and only one Basket can be purchased at a time. If you would like to order this Basket, please remove the other item(s) from your cart first, then add this Basket and proceed to checkout.';

/** Shown on product forms while a Basket is in the cart (edit freely). */
const BASKET_IN_CART_NOTICE_TEXT =
  'Your cart contains a Basket. Only one basket and no other items may be shipped in a box. If ordering multiple baskets please complete this purchase first.';

/** Shown above the checkout buttons while a Basket and other items are both in the cart. */
const CHECKOUT_NOTICE_TEXT =
  'Your cart contains a Basket together with other items. Basket products must be ordered separately, and only one Basket can be purchased at a time. Please remove either the other item(s) or the Basket from your cart to proceed to checkout.';

const CHECKOUT_NOTICE_CLASS = 'basket-checkout-notice';
const CHECKOUT_DISABLED_ATTR = 'data-basket-checkout-disabled';
const CTA_SELECTOR = '.cart__ctas';
const CHECKOUT_BUTTON_SELECTOR =
  'button[name="checkout"], #checkout, .cart__checkout-button, button[type="submit"]';
const WALLET_CONTAINER_SELECTOR =
  '.additional-checkout-buttons, .dynamic-checkout__content';
const CART_FORM_SELECTOR =
  'form[action*="/cart"]:not([action*="/cart/add"])';

const NOTICE_CLASS = 'basket-atc-notice';
const DISABLED_ATTR = 'data-basket-atc-disabled';
const NOTICE_MODE_ATTR = 'data-basket-atc-notice-mode';
const TYPE_ATTR = 'data-product-type';
const HANDLE_ATTR = 'data-product-handle';

const INITIAL_DISABLED_CLASS = 'product-form-buttons--disabled';
const QUANTITY_DISABLED_ATTR = 'data-basket-quantity-disabled';

const QUANTITY_CONTROL_SELECTOR = [
  'quantity-selector-component button',
  'quantity-selector-component input',
  '.quantity-selector button',
  '.quantity-selector input',
].join(', ');

const ADD_FORM_SELECTOR = 'form[action*="/cart/add"]';

const ADD_BUTTON_SELECTOR = [
  `${ADD_FORM_SELECTOR} button[type="submit"]`,
  `${ADD_FORM_SELECTOR} button[name="add"]`,
  `${ADD_FORM_SELECTOR} input[type="submit"]`,
  'button[name="add"]',
  'add-to-cart-component',
  'add-to-cart-component button',
  'product-form-component button[type="submit"]',
  '.product-form__submit',
  '.quick-add__submit',
  '.quick-add__button',
  '.quick-add-button',
  '[data-add-to-cart]',
  '[data-quick-add]',
].join(', ');

/** Anything inside these is considered cart markup for the observer. */
const CART_SCOPE_SELECTOR = [
  'cart-items-component',
  'cart-drawer-component',
  'cart-page-component',
  'cart-quantity-selector-component',
  '.cart__ctas',
  'form[action*="/cart"]',
  '#cart-form',
  '.cart-count-bubble',
  'cart-icon',
].join(', ');

/** Candidate theme event names; unknown ones are simply never fired. */
const FALLBACK_EVENT_NAMES = [
  'cart:update',
  'cart:lines-update',
  'cart:refresh',
  'cart:added',
];

const CART_ENDPOINT_PATTERN =
  /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

const REFRESH_DEBOUNCE_MS = 60;

/** @type {{ hasBasket: boolean, hasOther: boolean, checkoutConflict: boolean }} */
let cartState = {
  hasBasket: false,
  hasOther: false,
  checkoutConflict: false,
};

let cartReady = false;

/** @type {Map<string, Promise<string>>} product handle -> product type (lower-cased) */
const typeCache = new Map();

/** @type {Map<string, string>} product id -> product type, filled by handle lookups */
const typeByProductId = new Map();

/** @type {MutationObserver | null} */
let observer = null;
let refreshTimer = 0;
let requestSequence = 0;
let applySequence = 0;

/* ------------------------------------------------------------------ */
/* Cart state                                                          */
/* ------------------------------------------------------------------ */

function routeRoot() {
  return window.Shopify?.routes?.root ?? '/';
}

/**
 * @param {string | undefined | null} type
 */
function isBasketType(type) {
  return BASKET_TYPES.has((type ?? '').trim().toLowerCase());
}

async function fetchCartState() {
  const response = await fetch(`${routeRoot()}cart.js`, {
    headers: {
      Accept: 'application/json',
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`cart.js responded ${response.status}`);
  }

  const cart = await response.json();
  const items = Array.isArray(cart.items) ? cart.items : [];

  let hasBasket = false;
  let hasOther = false;

  items.forEach((item) => {
    if ((Number(item.quantity) || 0) <= 0) return;

    if (isBasketType(item.product_type)) {
      hasBasket = true;
    } else {
      hasOther = true;
    }
  });

  return {
    hasBasket,
    hasOther,
    checkoutConflict: hasBasket && hasOther,
  };
}

/* ------------------------------------------------------------------ */
/* Product type resolution                                             */
/* ------------------------------------------------------------------ */

/**
 * @param {string} href
 */
function handleFromUrl(href) {
  const match = /\/products\/([^/?#]+)/.exec(href ?? '');

  return match
    ? decodeURIComponent(match[1])
    : '';
}

/** Card-level containers; the handle search never climbs past one of these. */
const CARD_BOUNDARY_SELECTOR = [
  'product-card',
  'product-card-component',
  'quick-add-dialog',
  '.product-card',
  '.card-wrapper',
  '.card',
  '.grid__item',
  '.product-item',
  '.product-grid__item',
  'li',
].join(', ');

/** Never search above one of these (would pick up other products' links). */
const SEARCH_STOP_SELECTOR =
  '.shopify-section, section, main, body';

/** Attributes that may carry a product URL or handle on an element. */
const URL_ATTRS = [
  HANDLE_ATTR,
  'data-product-url',
  'data-url',
  'data-href',
  'href',
];

/**
 * @param {Element} element
 */
function handleFromAttrs(element) {
  for (const attr of URL_ATTRS) {
    const value = element.getAttribute(attr);

    if (!value) continue;

    const handle =
      attr === HANDLE_ATTR
        ? value.trim()
        : handleFromUrl(value);

    if (handle) {
      return handle;
    }
  }

  return '';
}

/**
 * Distinct product handles linked from inside an element.
 * @param {Element} element
 */
function linkedHandles(element) {
  const handles = new Set();

  element
    .querySelectorAll(
      'a[href*="/products/"], [data-product-url], [data-product-handle]'
    )
    .forEach((el) => {
      const handle = handleFromAttrs(el);

      if (handle) {
        handles.add(handle);
      }
    });

  return handles;
}

/**
 * Walk up from the button until a single product can be identified.
 * @param {Element} button
 */
function findHandle(button) {
  const urlInput = button
    .closest(ADD_FORM_SELECTOR)
    ?.querySelector(
      'input[name="product-url"], input[name="product_url"]'
    );

  if (
    urlInput instanceof HTMLInputElement &&
    urlInput.value
  ) {
    const fromInput = handleFromUrl(urlInput.value);

    if (fromInput) {
      return fromInput;
    }
  }

  /** @type {Element | null} */
  let element = button;

  while (
    element &&
    !element.matches(SEARCH_STOP_SELECTOR)
  ) {
    const own = handleFromAttrs(element);

    if (own) {
      return own;
    }

    const handles = linkedHandles(element);

    if (handles.size === 1) {
      return handles.values().next().value;
    }

    if (handles.size > 1) {
      return '';
    }

    if (
      element !== button &&
      element.matches(CARD_BOUNDARY_SELECTOR)
    ) {
      break;
    }

    element = element.parentElement;
  }

  return '';
}

/**
 * True when the form's selected variant belongs to the product of the current page.
 * @param {Element} button
 */
function isMainProductForm(button) {
  const meta =
    window.ShopifyAnalytics?.meta?.product;

  if (!meta) {
    return false;
  }

  const form =
    button.closest(ADD_FORM_SELECTOR) ??
    button.closest('product-form-component');

  const idInput =
    form?.querySelector('input[name="id"]');

  const variantId =
    idInput instanceof HTMLInputElement
      ? String(idInput.value)
      : '';

  if (!variantId) {
    return false;
  }

  const variants =
    Array.isArray(meta.variants)
      ? meta.variants
      : [];

  return variants.some(
    (variant) =>
      String(variant.id) === variantId
  );
}

/**
 * @param {string} handle
 */
function fetchProductType(handle) {
  if (!typeCache.has(handle)) {
    const promise = fetch(
      `${routeRoot()}products/${encodeURIComponent(handle)}.js`,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    )
      .then((response) =>
        response.ok
          ? response.json()
          : null
      )
      .then((product) => {
        const type =
          (product?.type ?? '')
            .trim()
            .toLowerCase();

        if (product?.id != null) {
          typeByProductId.set(
            String(product.id),
            type
          );
        }

        return type;
      })
      .catch(() => {
        typeCache.delete(handle);
        return '';
      });

    typeCache.set(handle, promise);
  }

  return typeCache.get(handle);
}

/**
 * Resolve the product type for an Add to Cart button. Unknown types resolve to ''.
 * @param {Element} button
 */
async function resolveProductType(button) {
  const typed =
    button.closest(`[${TYPE_ATTR}]`);

  const fromAttr =
    typed?.getAttribute(TYPE_ATTR);

  if (fromAttr) {
    return fromAttr
      .trim()
      .toLowerCase();
  }

  const meta =
    window.ShopifyAnalytics?.meta?.product;

  if (
    meta &&
    isMainProductForm(button)
  ) {
    return (meta.type ?? '')
      .trim()
      .toLowerCase();
  }

  const handle = findHandle(button);

  if (handle) {
    return fetchProductType(handle);
  }

  const idHolder =
    button.closest('[data-product-id]');

  const productId =
    idHolder?.getAttribute('data-product-id') ?? '';

  return typeByProductId.get(productId) ?? '';
}

/* ------------------------------------------------------------------ */
/* DOM application                                                     */
/* ------------------------------------------------------------------ */

/**
 * @param {Element} button
 */
function noticeAnchor(button) {
  return (
    button.closest(ADD_FORM_SELECTOR) ??
    button.closest('product-form-component') ??
    button.closest('add-to-cart-component') ??
    button
  );
}

/**
 * @param {Element} button
 */
function isQuickAdd(button) {
  return (
    button.matches(
      '.quick-add__submit, .quick-add__button, .quick-add-button, [data-quick-add]'
    ) ||
    button.closest(
      'quick-add-component, .quick-add, .quick-add__button, product-card, product-card-component, .product-card, .card-wrapper, .card, .grid__item, .product-item, .product-grid__item'
    ) !== null
  );
}

/**
 * @param {Element} anchor
 */
function findNotice(anchor) {
  const previous =
    anchor.previousElementSibling;

  return previous?.classList.contains(
    NOTICE_CLASS
  )
    ? previous
    : null;
}

/**
 * @param {Element} anchor
 * @param {string} text
 * @param {string} mode
 */
function ensureNotice(
  anchor,
  text,
  mode
) {
  const existing =
    findNotice(anchor);

  if (existing) {
    if (
      existing.getAttribute(
        NOTICE_MODE_ATTR
      ) !== mode
    ) {
      existing.textContent = text;

      existing.setAttribute(
        NOTICE_MODE_ATTR,
        mode
      );
    }

    return;
  }

  const notice =
    document.createElement('p');

  notice.className = NOTICE_CLASS;
  notice.setAttribute(
    'role',
    'alert'
  );

  notice.setAttribute(
    NOTICE_MODE_ATTR,
    mode
  );

  notice.textContent = text;

  anchor.before(notice);
}

/**
 * @param {Element} button
 * @param {string} text
 * @param {string} mode
 */
function disableButton(
  button,
  text,
  mode
) {
  if (
    button instanceof HTMLButtonElement ||
    button instanceof HTMLInputElement
  ) {
    button.disabled = true;
  }

  button.setAttribute(
    'aria-disabled',
    'true'
  );

  button.setAttribute(
    DISABLED_ATTR,
    mode
  );

  const anchor =
    noticeAnchor(button);

  if (isQuickAdd(button)) {
    button.setAttribute(
      'title',
      text
    );

    findNotice(anchor)?.remove();
  } else {
    ensureNotice(
      anchor,
      text,
      mode
    );
  }
}

/**
 * Only undo what we did ourselves; never touch buttons the theme disabled (sold out etc).
 * @param {Element} button
 */
function enableButton(button) {
  if (
    !button.hasAttribute(
      DISABLED_ATTR
    )
  ) {
    return;
  }

  if (
    button instanceof HTMLButtonElement ||
    button instanceof HTMLInputElement
  ) {
    button.disabled = false;
  }

  button.removeAttribute(
    'aria-disabled'
  );

  button.removeAttribute(
    DISABLED_ATTR
  );

  button.removeAttribute(
    'title'
  );

  findNotice(
    noticeAnchor(button)
  )?.remove();
}

/**
 * @param {Element} cta
 */
function findCheckoutNotice(cta) {
  const previous =
    cta.previousElementSibling;

  return previous?.classList.contains(
    CHECKOUT_NOTICE_CLASS
  )
    ? previous
    : null;
}

/**
 * Liquid initially adds:
 * product-form-buttons--disabled
 *
 * Once /cart.js has loaded successfully,
 * JavaScript becomes the source of truth.
 */
function clearInitialDisabledState() {
  document
    .querySelectorAll(
      `.${INITIAL_DISABLED_CLASS}`
    )
    .forEach((element) => {
      element.classList.remove(
        INITIAL_DISABLED_CLASS
      );
    });
}

/**
 * Disable Gift Basket quantity controls
 * while a Basket already exists in cart.
 */
function applyBasketQuantityRules() {
  document
    .querySelectorAll(
      `[${QUANTITY_DISABLED_ATTR}]`
    )
    .forEach((control) => {
      if (
        control instanceof HTMLButtonElement ||
        control instanceof HTMLInputElement
      ) {
        control.disabled = false;
      }

      control.removeAttribute(
        'aria-disabled'
      );

      control.removeAttribute(
        QUANTITY_DISABLED_ATTR
      );
    });

  if (!cartState.hasBasket) {
    return;
  }

  document
    .querySelectorAll(
      `[${TYPE_ATTR}]`
    )
    .forEach((typed) => {
      if (
        !isBasketType(
          typed.getAttribute(TYPE_ATTR)
        )
      ) {
        return;
      }

      const scope =
        typed.closest(
          'product-form-component, product-information, .product-information, [data-product-id]'
        ) ?? typed;

      scope
        .querySelectorAll(
          QUANTITY_CONTROL_SELECTOR
        )
        .forEach((control) => {
          if (
            !(control instanceof HTMLButtonElement) &&
            !(control instanceof HTMLInputElement)
          ) {
            return;
          }

          if (control.disabled) {
            return;
          }

          control.disabled = true;

          control.setAttribute(
            'aria-disabled',
            'true'
          );

          control.setAttribute(
            QUANTITY_DISABLED_ATTR,
            ''
          );
        });
    });
}

function applyCheckoutRules() {
  document
    .querySelectorAll(
      CTA_SELECTOR
    )
    .forEach((cta) => {
      const checkoutButtons =
        cta.querySelectorAll(
          CHECKOUT_BUTTON_SELECTOR
        );

      const walletContainers =
        cta.querySelectorAll(
          WALLET_CONTAINER_SELECTOR
        );

      if (
        cartState.checkoutConflict
      ) {
        checkoutButtons.forEach(
          (button) => {
            if (
              button instanceof HTMLButtonElement ||
              button instanceof HTMLInputElement
            ) {
              button.disabled = true;
            }

            button.setAttribute(
              'aria-disabled',
              'true'
            );

            button.setAttribute(
              CHECKOUT_DISABLED_ATTR,
              ''
            );
          }
        );

        walletContainers.forEach(
          (container) => {
            container.inert = true;

            container.setAttribute(
              CHECKOUT_DISABLED_ATTR,
              ''
            );
          }
        );

        if (
          !findCheckoutNotice(cta)
        ) {
          const notice =
            document.createElement('p');

          notice.className =
            CHECKOUT_NOTICE_CLASS;

          notice.setAttribute(
            'role',
            'alert'
          );

          notice.textContent =
            CHECKOUT_NOTICE_TEXT;

          cta.before(notice);
        }

        cta.setAttribute(
          CHECKOUT_DISABLED_ATTR,
          ''
        );

        return;
      }

      checkoutButtons.forEach(
        (button) => {
          if (
            !button.hasAttribute(
              CHECKOUT_DISABLED_ATTR
            )
          ) {
            return;
          }

          if (
            button instanceof HTMLButtonElement ||
            button instanceof HTMLInputElement
          ) {
            button.disabled = false;
          }

          button.removeAttribute(
            'aria-disabled'
          );

          button.removeAttribute(
            CHECKOUT_DISABLED_ATTR
          );
        }
      );

      walletContainers.forEach(
        (container) => {
          if (
            !container.hasAttribute(
              CHECKOUT_DISABLED_ATTR
            )
          ) {
            return;
          }

          container.inert = false;

          container.removeAttribute(
            CHECKOUT_DISABLED_ATTR
          );
        }
      );

      findCheckoutNotice(
        cta
      )?.remove();

      cta.removeAttribute(
        CHECKOUT_DISABLED_ATTR
      );
    });
}

async function applyAddToCartRules() {
  const sequence =
    ++applySequence;

  const buttons =
    Array.from(
      document.querySelectorAll(
        ADD_BUTTON_SELECTOR
      )
    );

  if (
    buttons.length === 0
  ) {
    return;
  }

  /*
   * Basket already in cart:
   * disable all Add to Cart buttons.
   */
  if (cartState.hasBasket) {
    buttons.forEach(
      (button) =>
        disableButton(
          button,
          BASKET_IN_CART_NOTICE_TEXT,
          'basket-in-cart'
        )
    );

    observer?.takeRecords();

    return;
  }

  /*
   * Empty cart:
   * normal Add to Cart functionality.
   */
  if (!cartState.hasOther) {
    buttons.forEach(
      enableButton
    );

    observer?.takeRecords();

    return;
  }

  /*
   * Normal product already in cart:
   * disable only Basket Add to Cart buttons.
   */
  const types =
    await Promise.all(
      buttons.map((button) =>
        resolveProductType(button)
          .catch(() => '')
      )
    );

  if (
    sequence !== applySequence
  ) {
    return;
  }

  buttons.forEach(
    (button, index) => {
      if (!button.isConnected) {
        return;
      }

      if (
        isBasketType(types[index])
      ) {
        disableButton(
          button,
          CONFLICT_NOTICE_TEXT,
          'conflict'
        );
      } else {
        enableButton(button);
      }
    }
  );

  observer?.takeRecords();
}

function apply() {
  /*
   * Do not remove the Liquid fallback
   * until /cart.js has loaded.
   */
  if (!cartReady) {
    return;
  }

  /*
   * JavaScript now takes control.
   */
  clearInitialDisabledState();

  applyCheckoutRules();
  applyBasketQuantityRules();
  applyAddToCartRules();

  observer?.takeRecords();
}

/* ------------------------------------------------------------------ */
/* Refresh scheduling                                                  */
/* ------------------------------------------------------------------ */

async function refresh() {
  const sequence =
    ++requestSequence;

  try {
    const nextState =
      await fetchCartState();

    if (
      sequence !== requestSequence
    ) {
      return;
    }

    cartState = nextState;
    cartReady = true;
  } catch (error) {
    console.warn(
      '[basket-add-to-cart-rules] Could not read cart:',
      error
    );

    /*
     * Keep Liquid fallback disabled
     * if cart state cannot be loaded.
     */
    return;
  }

  apply();
}

function scheduleRefresh() {
  window.clearTimeout(
    refreshTimer
  );

  refreshTimer =
    window.setTimeout(
      refresh,
      REFRESH_DEBOUNCE_MS
    );
}

/** Re-apply against the current state without re-fetching (new markup rendered). */
function scheduleApply() {
  window.clearTimeout(
    refreshTimer
  );

  refreshTimer =
    window.setTimeout(
      apply,
      REFRESH_DEBOUNCE_MS
    );
}

/* ------------------------------------------------------------------ */
/* Triggers                                                            */
/* ------------------------------------------------------------------ */

/**
 * @param {Node} node
 * @param {string} selector
 */
function nodeTouches(
  node,
  selector
) {
  if (
    !(node instanceof Element)
  ) {
    return false;
  }

  return (
    node.matches(selector) ||
    node.querySelector(selector) !== null
  );
}

/**
 * @param {MutationRecord} mutation
 */
function isCartMutation(mutation) {
  const target =
    mutation.target instanceof Element
      ? mutation.target
      : mutation.target.parentElement;

  if (
    target?.closest(
      CART_SCOPE_SELECTOR
    )
  ) {
    return true;
  }

  for (
    const node of mutation.addedNodes
  ) {
    if (
      nodeTouches(
        node,
        CART_SCOPE_SELECTOR
      )
    ) {
      return true;
    }
  }

  for (
    const node of mutation.removedNodes
  ) {
    if (
      nodeTouches(
        node,
        CART_SCOPE_SELECTOR
      )
    ) {
      return true;
    }
  }

  return false;
}

/**
 * @param {MutationRecord} mutation
 */
function isProductFormMutation(mutation) {
  const target =
    mutation.target instanceof Element
      ? mutation.target
      : mutation.target.parentElement;

  if (
    mutation.type === 'attributes'
  ) {
    return (
      target?.matches(
        ADD_BUTTON_SELECTOR
      ) ||
      target?.matches(
        'input[name="id"]'
      ) ||
      false
    );
  }

  for (
    const node of mutation.addedNodes
  ) {
    if (
      nodeTouches(
        node,
        ADD_BUTTON_SELECTOR
      )
    ) {
      return true;
    }
  }

  return false;
}

function observeMarkup() {
  observer =
    new MutationObserver(
      (mutations) => {
        if (
          mutations.some(
            isCartMutation
          )
        ) {
          scheduleRefresh();
        } else if (
          mutations.some(
            isProductFormMutation
          )
        ) {
          scheduleApply();
        }
      }
    );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'disabled',
        'inert',
        'value',
        'data-cart-quantity',
        'data-variant-id',
        HANDLE_ATTR,
        TYPE_ATTR,
      ],
    }
  );
}

/**
 * @param {Event & { promise?: Promise<unknown> }} event
 */
function handleCartEvent(event) {
  const promise =
    event.promise;

  if (
    promise &&
    typeof promise.then === 'function'
  ) {
    promise.then(
      scheduleRefresh,
      scheduleRefresh
    );
  } else {
    scheduleRefresh();
  }
}

function listenToCartEvents() {
  const names =
    new Set(
      FALLBACK_EVENT_NAMES
    );

  import('@shopify/events')
    .then(
      ({ StandardEvents }) => {
        [
          StandardEvents?.cartUpdate,
          StandardEvents?.cartLinesUpdate,
          StandardEvents?.cartAdded,
        ]
          .filter(Boolean)
          .forEach((name) => {
            if (
              names.has(name)
            ) {
              return;
            }

            names.add(name);

            document.addEventListener(
              name,
              handleCartEvent
            );
          });
      }
    )
    .catch(() => {
      /*
       * No import map entry for
       * @shopify/events.
       * Fetch interception and observer
       * still handle cart changes.
       */
    });

  FALLBACK_EVENT_NAMES.forEach(
    (name) =>
      document.addEventListener(
        name,
        handleCartEvent
      )
  );
}

/**
 * Refresh after any cart AJAX call.
 */
function watchCartRequests() {
  const nativeFetch =
    window.fetch;

  if (
    typeof nativeFetch !== 'function'
  ) {
    return;
  }

  window.fetch =
    function patchedFetch(
      input,
      init
    ) {
      const url =
        typeof input === 'string'
          ? input
          : input?.url ??
            String(input ?? '');

      const result =
        nativeFetch.call(
          this,
          input,
          init
        );

      if (
        CART_ENDPOINT_PATTERN.test(
          url
        )
      ) {
        result.then(
          scheduleRefresh,
          scheduleRefresh
        );
      }

      return result;
    };
}

function blockRestrictedAdds() {
  document.addEventListener(
    'submit',
    (event) => {
      const form =
        event.target;

      if (
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }

      /*
       * Block Add to Cart form submit
       * when JS has disabled it.
       */
      if (
        form.matches(
          ADD_FORM_SELECTOR
        ) &&
        form.querySelector(
          `[${DISABLED_ATTR}]`
        )
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();

        return;
      }

      /*
       * Block checkout when Basket
       * and normal products conflict.
       */
      if (
        cartState.checkoutConflict &&
        form.matches(
          CART_FORM_SELECTOR
        )
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      const button =
        target.closest(
          `[${DISABLED_ATTR}], [${CHECKOUT_DISABLED_ATTR}]`
        );

      if (!button) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );
}

function injectStyles() {
  if (
    document.getElementById(
      'basket-add-to-cart-rules-styles'
    )
  ) {
    return;
  }

  const style =
    document.createElement('style');

  style.id =
    'basket-add-to-cart-rules-styles';

  style.textContent = `
    .${NOTICE_CLASS} {
      margin: 0 0 var(--gap-sm, 0.75rem);
      padding: 0.75rem 1rem;
      border: 1px solid currentColor;
      border-radius: 4px;
      font-size: var(--font-size--sm, 0.875rem);
      line-height: 1.4;
      color: #b21b4b;
    }

    .${CHECKOUT_NOTICE_CLASS} {
      margin: 0 0 var(--gap-sm, 0.75rem);
      padding: 0.75rem 1rem;
      border: 1px solid currentColor;
      border-radius: 4px;
      font-size: var(--font-size--sm, 0.875rem);
      line-height: 1.4;
      color: #b21b4b;
    }

    [${DISABLED_ATTR}],
    ${CTA_SELECTOR} [${CHECKOUT_DISABLED_ATTR}] {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  `;

  document.head.appendChild(
    style
  );
}

/* ------------------------------------------------------------------ */
/* Boot                                                                */
/* ------------------------------------------------------------------ */

function init() {
  window.basketAtcRules = {
    refresh,

    state: () => cartState,

    debug: async () => {
      console.log(
        '[basket-add-to-cart-rules] cart state',
        cartState
      );

      for (
        const button of document.querySelectorAll(
          ADD_BUTTON_SELECTOR
        )
      ) {
        console.log(
          button,
          'handle:',
          findHandle(button) || '(none)',
          'type:',
          await resolveProductType(button) || '(unknown)'
        );
      }
    },
  };

  injectStyles();
  watchCartRequests();
  observeMarkup();
  listenToCartEvents();
  blockRestrictedAdds();

  /*
   * First /cart.js read.
   * Until this completes, the Liquid class
   * keeps the product buttons disabled.
   */
  refresh();

  window.addEventListener(
    'pageshow',
    (event) => {
      if (event.persisted) {
        scheduleRefresh();
      }
    }
  );
}

if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    init,
    {
      once: true,
    }
  );
} else {
  init();
}